import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { APP_VERSION, PRODUCT_NAME } from '@learnwithai/ui-shared';
import { parentApi, SIGNED_OUT_ELSEWHERE_KEY, storeRefreshToken, type ApiError } from '../api';
import PasskeyButton from '../components/PasskeyButton';
import { PUBLIC_LANDING_ENABLED } from '../publicLaunchFlag';

export default function LoginPage() {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language?.startsWith('fr');
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  // Legacy-MFA fallback: enrollment is disabled server-side (410), but accounts
  // that enabled TOTP before the freeze still get MfaRequired=true from
  // /auth/login — they must be able to enter their code, not silently stall.
  const [mfaCode, setMfaCode] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Single active sign-in: 409 session-conflict from login → offer takeover.
  const [sessionConflict, setSessionConflict] = useState(false);
  const [signedOutElsewhere, setSignedOutElsewhere] = useState(false);

  // If the API signed us out because this account was used on another device,
  // explain that instead of leaving the user guessing.
  useEffect(() => {
    if (sessionStorage.getItem(SIGNED_OUT_ELSEWHERE_KEY) === '1') {
      sessionStorage.removeItem(SIGNED_OUT_ELSEWHERE_KEY);
      setSignedOutElsewhere(true);
    }
  }, []);

  const doLogin = async (force: boolean) => {
    setError('');
    setSessionConflict(false);
    setLoading(true);
    try {
      const res = await parentApi.login(email, password, mfaRequired ? mfaCode : undefined, force || undefined, rememberMe);

      if (res.mfaRequired && !res.token) {
        setMfaRequired(true);
        return;
      }

      if (res.token) {
        localStorage.setItem('auth_token', res.token);
        storeRefreshToken(rememberMe ? res.refreshToken : undefined);
        navigate('/dashboard');
      }
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      if (apiErr?.code === 'session-conflict') {
        setSessionConflict(true);
        setError(t('auth.sessionConflict', 'Another device is signed in with this email.'));
      } else if (apiErr?.status === 401) {
        setError(t('auth.invalidCredentials', 'Incorrect email or password.'));
      } else {
        setError(err instanceof Error ? err.message : t('auth.loginError', 'Login failed. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    await doLogin(false);
  };

  const P = { indigo:'#5B4FE8', coral:'#FF6B50', dark:'#1A1035', text:'#18181B', sub:'#6B7280', border:'#E5E7EB', bg:'#FAFAFA' };

  return (
    <div style={{ minHeight:'100vh', background: P.dark, display:'flex', alignItems:'center', justifyContent:'center', padding:24, flexDirection:'column' as const }}>
      {/* Always-visible language toggle: every INITIAL page must let a family
          switch EN↔FR before doing anything else (FR-tester feedback 2026-09-06). */}
      <button data-testid="lang-toggle" onClick={() => void i18n.changeLanguage(isFr ? 'en-US' : 'fr-FR')}
        style={{ position:'fixed', top:16, right:16, zIndex:50, background:'none', border:'1px solid rgba(255,255,255,0.35)', borderRadius:8, padding:'5px 12px', fontSize:13, fontWeight:700, color:'rgba(255,255,255,0.85)', cursor:'pointer', whiteSpace:'nowrap' }}>
        {isFr ? '🇬🇧 EN' : '🇫🇷 FR'}
      </button>
      <style>{`
        .lp-input { background:rgba(255,255,255,0.07)!important; border:1px solid rgba(255,255,255,0.15)!important; color:#fff!important; border-radius:12px!important; padding:14px 16px!important; font-size:1rem!important; width:100%; outline:none; transition:border 0.2s; }
        .lp-input::placeholder { color:rgba(255,255,255,0.35); }
        .lp-input:focus { border-color:#5B4FE8!important; box-shadow:0 0 0 3px rgba(91,79,232,0.25); }
        .lp-btn-primary { width:100%; padding:14px; border:none; border-radius:100px; background:linear-gradient(135deg,#5B4FE8 0%,#7C3AED 60%,#FF6B50 100%); background-size:200% auto; color:#fff; font-weight:800; font-size:1rem; cursor:pointer; box-shadow:0 8px 32px rgba(91,79,232,0.40); transition:box-shadow 0.2s,transform 0.2s; }
        .lp-btn-primary:hover { box-shadow:0 12px 40px rgba(91,79,232,0.55); transform:translateY(-1px); }
        .lp-btn-primary:disabled { opacity:0.6; cursor:not-allowed; }
        @keyframes lpFadeIn { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      {/* Logo */}
      <div style={{ marginBottom:32, textAlign:'center', animation:'lpFadeIn 0.4s ease' }}>
        <div style={{ display:'inline-flex', alignItems:'center', gap:10, marginBottom:8 }}>
          <div style={{ width:40, height:40, borderRadius:12, background:'linear-gradient(135deg,#5B4FE8,#FF6B50)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:900, fontSize:22 }}>R</div>
          <span style={{ fontWeight:900, fontSize:'1.4rem', color:'#fff', letterSpacing:'-0.02em' }}>{PRODUCT_NAME}</span>
        </div>
        <p style={{ color:'rgba(255,255,255,0.55)', fontSize:'0.9rem' }}>{t('auth.portalSubtitle', 'Parent & Teacher Portal')}</p>
      </div>

      <div style={{ width:'100%', maxWidth:420, background:'rgba(255,255,255,0.05)', backdropFilter:'blur(20px)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:24, padding:40, animation:'lpFadeIn 0.45s ease' }}>
        <h2 style={{ color:'#fff', fontWeight:800, fontSize:'1.5rem', marginBottom:6, letterSpacing:'-0.02em' }}>{t('auth.login')}</h2>
        <p style={{ color:'rgba(255,255,255,0.55)', fontSize:'0.9rem', marginBottom:28 }}>{t('auth.welcomeBack', "Welcome back — your student's progress awaits.")}</p>

        {signedOutElsewhere && (
          <p style={{ color:'#FFD166', fontSize:'0.88rem', background:'rgba(255,209,102,0.10)', borderRadius:10, padding:'10px 14px', border:'1px solid rgba(255,209,102,0.30)', marginBottom:16 }} role="status">
            {t('auth.signedOutElsewhere', 'You were signed out because this account was used on another device.')}
          </p>
        )}

        <form onSubmit={handleLogin} style={{ display:'flex', flexDirection:'column', gap:14 }} autoComplete="on">
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            <label htmlFor="login-email" style={{ fontSize:'0.82rem', fontWeight:600, color:'rgba(255,255,255,0.65)', letterSpacing:'0.04em' }}>{t('auth.email')}</label>
            <input id="login-email" aria-label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="lp-input" autoComplete="username email" disabled={mfaRequired} />
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            <label htmlFor="login-password" style={{ fontSize:'0.82rem', fontWeight:600, color:'rgba(255,255,255,0.65)', letterSpacing:'0.04em' }}>{t('auth.password')}</label>
            <input id="login-password" aria-label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="lp-input" autoComplete="current-password" disabled={mfaRequired} />
          </div>
          {mfaRequired && (
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              <label style={{ fontSize:'0.82rem', fontWeight:600, color:'rgba(255,255,255,0.65)', letterSpacing:'0.04em' }}>🔐 {t('auth.mfaCode')}</label>
              <input type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g,''))} placeholder="000000" className="lp-input" autoFocus autoComplete="one-time-code" />
            </div>
          )}
          <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:'0.85rem', color:'rgba(255,255,255,0.65)', cursor:'pointer' }}>
            <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} style={{ width:16, height:16, accentColor:'#5B4FE8' }} />
            {t('auth.rememberMe', 'Keep me signed in')}
          </label>
          {error && <p style={{ color:'#FF6B50', fontSize:'0.88rem', background:'rgba(255,107,80,0.12)', borderRadius:10, padding:'10px 14px', border:'1px solid rgba(255,107,80,0.25)' }} role="alert">{error}</p>}
          {sessionConflict && (
            <button
              type="button"
              className="lp-btn-primary"
              disabled={loading}
              onClick={() => doLogin(true)}
              style={{ background:'linear-gradient(135deg,#FF6B50 0%,#7C3AED 100%)' }}
            >
              {loading ? '...' : t('auth.signInHereInstead', 'Sign in here instead')}
            </button>
          )}
          <button type="submit" className="lp-btn-primary" disabled={loading} style={{ marginTop:6 }}>
            {loading ? '...' : mfaRequired ? t('auth.verifyCode', 'Verify Code') : t('auth.login')}
          </button>
        </form>

        {/* Honest recovery affordance (Codex #9): recovery codes don't exist yet
            (MFA enrollment + /auth/mfa/recover are disabled server-side, 410), so
            never advertise "Use a recovery code" — it was a dead end. Rendered
            ONLY when the server actually asked for an MFA code (legacy accounts);
            support is the one real path until QR enrollment + recovery codes ship. */}
        {mfaRequired && (
          <p style={{ textAlign:'center', marginTop:12, fontSize:'0.85rem', color:'rgba(255,255,255,0.45)' }}>
            {t('auth.lostAuthenticator', 'Lost your authenticator?')}{' '}<a href="/support" style={{ color:'#5B4FE8' }}>{t('auth.contactSupport', 'Contact support')}</a>
          </p>
        )}

        <div style={{ display:'flex', alignItems:'center', gap:12, margin:'24px 0 16px' }}>
          <span style={{ flex:1, height:1, background:'rgba(255,255,255,0.10)' }} />
          <span style={{ fontSize:'0.8rem', color:'rgba(255,255,255,0.35)', whiteSpace:'nowrap' }}>{t('auth.orContinueWith', 'or continue with')}</span>
          <span style={{ flex:1, height:1, background:'rgba(255,255,255,0.10)' }} />
        </div>

        <PasskeyButton mode="login" email={email}
          onSuccess={(token) => { if (token) { localStorage.setItem('auth_token', token); navigate('/dashboard'); } }}
          onError={(msg) => setError(msg)}
        />

        <div style={{ marginTop:24, textAlign:'center' }}>
          <a href="/forgot-password" style={{ color:'rgba(255,255,255,0.50)', fontSize:'0.88rem', textDecoration:'none' }}>{t('auth.forgotPassword')}</a>
        </div>

        {/* Open signup: self-serve account creation lives at /register. Hidden
            pre-launch (PUBLIC_LANDING_ENABLED) so a logged-out visitor isn't
            invited to a still-walled signup; direct /register access still
            works for invited testers. */}
        {PUBLIC_LANDING_ENABLED && (
        <p style={{ marginTop:16, marginBottom:0, textAlign:'center', fontSize:'0.88rem', color:'rgba(255,255,255,0.45)' }}>
          {isFr ? `Nouveau sur ${PRODUCT_NAME} ?` : `New to ${PRODUCT_NAME}?`}{' '}
          <a href="/register" style={{ color:'#5B4FE8', textDecoration:'none', fontWeight:600 }}>
            {isFr ? 'Créer un compte' : 'Create an account'}
          </a>
        </p>
        )}
      </div>
      <p style={{ fontSize:'0.6rem', color:'rgba(255,255,255,0.20)', marginTop:20, textAlign:'center' }}>v{APP_VERSION}</p>
    </div>
  );
}
